/**
 * サムネイルを並べるだけの Home ウィジェットの枠（ショートカット・履歴）。
 *
 * 1 マスに 1 枚の割合で行×列ぶん並べる。1x1 は先頭の 1 枚、1 行・1 列は見出しを省いて一列に並べる。
 */

import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { WidgetEmpty, WidgetShell } from '@/components/home/widget-card'
import { cn } from '@/lib/utils'
import { parseWidgetSize, type WidgetSize } from '@/store/useHomeLayoutStore'

export type ThumbnailGridItem = {
  key: number
  title: string
  thumbnail: ReactNode
  onOpen: () => void
}

type ThumbnailGridWidgetProps = {
  items: ThumbnailGridItem[]
  size: WidgetSize
  icon: LucideIcon
  label: string
  accentClassName: string
  emptyMessage: string
  /** 1x1 で 1 件も無いときのタップ */
  onOpenEmpty: () => void
  /** 右下に重ねるもの。button の中に入るため button 以外で返す */
  renderOverlay?: (overflow: number) => ReactNode
}

const chunk = <T,>(items: T[], size: number): T[][] => {
  const result: T[][] = []
  for (let index = 0; index < items.length; index += size) result.push(items.slice(index, index + size))
  return result
}

export const ThumbnailGridWidget = ({
  items,
  size,
  icon: Icon,
  label,
  accentClassName,
  emptyMessage,
  onOpenEmpty,
  renderOverlay,
}: ThumbnailGridWidgetProps) => {
  const { rows: rowCount, cols: colCount } = parseWidgetSize(size)
  const visible = items.slice(0, rowCount * colCount)
  const overlay = renderOverlay?.(items.length - visible.length)

  if (rowCount === 1 && colCount === 1) {
    const first = visible.at(0)
    return (
      <button
        type="button"
        onClick={first ? first.onOpen : onOpenEmpty}
        aria-label={first ? first.title : label}
        className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-2xl border border-border/60 bg-card/60 p-0 active:opacity-70"
      >
        {first ? first.thumbnail : <Icon size={20} className="text-muted-foreground" />}
        {overlay}
      </button>
    )
  }

  if (rowCount === 1 || colCount === 1) {
    const vertical = colCount === 1
    return (
      <div
        className={cn(
          'relative flex h-full w-full items-center justify-center gap-1.5 overflow-hidden rounded-2xl border border-border/60 bg-card/60 p-2',
          vertical && 'flex-col',
        )}
      >
        {visible.length === 0 ? (
          <span className="text-[11px] text-muted-foreground">{emptyMessage}</span>
        ) : (
          visible.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={item.onOpen}
              aria-label={item.title}
              className={cn(
                'aspect-square overflow-hidden rounded-md border-none bg-muted p-0 active:opacity-70',
                vertical ? 'w-full' : 'h-full',
              )}
            >
              {item.thumbnail}
            </button>
          ))
        )}
        {overlay}
      </div>
    )
  }

  return (
    <WidgetShell icon={Icon} label={label} accentClassName={accentClassName} className="relative">
      {visible.length === 0 ? (
        <WidgetEmpty message={emptyMessage} />
      ) : (
        // 正方形は段の高さ基準。幅で割ると縦長・横長になる
        <div className="flex h-full flex-col justify-center gap-1.5">
          {chunk(visible, colCount).map((row, rowIndex) => (
            <div key={rowIndex} className="flex min-h-0 flex-1 justify-center gap-1.5">
              {row.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={item.onOpen}
                  aria-label={item.title}
                  className="aspect-square h-full overflow-hidden rounded-md border-none bg-muted p-0 active:opacity-70"
                >
                  {item.thumbnail}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
      {overlay}
    </WidgetShell>
  )
}
